import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError, STATUS_LABELS } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { StatusBadge } from "@/components/StatusBadge";
import { Send, Gavel, Receipt, Zap, Download, Trash2, AlertTriangle, RefreshCw, MailCheck, MailX, Mail } from "lucide-react";
import { Link } from "react-router-dom";

const STATUSES = ["nouveau", "envoye", "attente_confirmation", "confirme", "rejete", "arbitrage"];
const SOURCES = [
  ["", "Toutes origines"],
  ["reservation", "Réservation"],
  ["waitlist", "Liste d'attente"],
  ["contact", "Contact"],
  ["legacy", "Ancien formulaire"],
];
const SOURCE_STYLE = {
  reservation: "bg-blue-100 text-blue-700",
  waitlist: "bg-emerald-100 text-emerald-700",
  contact: "bg-amber-100 text-amber-700",
  "": "bg-neutral-100 text-neutral-600",
};

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [schools, setSchools] = useState([]);
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState("");
  const [city, setCity] = useState("");
  const [alreadyTrained, setAlreadyTrained] = useState(false);
  const [reassignMode, setReassignMode] = useState(false);
  const [relanceTargets, setRelanceTargets] = useState(null);
  const [sending, setSending] = useState(false);
  const [source, setSource] = useState("");
  const [selected, setSelected] = useState([]);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeScope, setPurgeScope] = useState("legacy");
  const [purgeText, setPurgeText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (status) params.status = status;
      if (city) params.city = city;
      if (alreadyTrained) params.already_trained = true;
      if (source) params.source = source;
      const [l, s, sc] = await Promise.all([
        reassignMode ? api.get("/admin/leads/to-reassign") : api.get("/admin/leads", { params }),
        api.get("/admin/stats"),
        api.get("/admin/schools"),
      ]);
      setLeads(l.data);
      setSelected((sel) => sel.filter((id) => l.data.some((x) => x.id === id)));
      setStats(s.data);
      setSchools(sc.data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  }, [status, city, alreadyTrained, reassignMode, source]);

  const toggle = (id) => setSelected((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));
  const allSelected = leads.length > 0 && selected.length === leads.length;

  const bulkDelete = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Supprimer définitivement ${selected.length} fiche(s) prospect ? (les réservations d'origine sont conservées)`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/leads/bulk-delete", { ids: selected });
      toast.success(`${data.deleted} fiche(s) supprimée(s)`);
      setSelected([]);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const purge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/leads/purge", { scope: purgeScope, confirm: purgeText });
      toast.success(`${data.deleted} fiche(s) supprimée(s)`);
      setPurgeOpen(false);
      setPurgeText("");
      setSelected([]);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const importDemandes = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/leads/import-demandes");
      toast.success(data.created ? `${data.created} nouvelle(s) demande(s) importée(s)` : "Tout est déjà à jour");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const action = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  const exportCsv = async (onlyDue) => {
    try {
      const res = await api.get("/admin/export/commissions", {
        params: { only_due: onlyDue },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = onlyDue ? "commissions_dues.csv" : "commissions.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <Shell role="admin">
      <div className="px-5 py-8 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tighter">Prospects</h1>
            <p className="mt-1 text-sm text-neutral-500">
              Chaque réservation, inscription en liste d&apos;attente et message de contact crée automatiquement une fiche ici et dans le CRM.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="import-demandes-btn"
              disabled={busy}
              onClick={importDemandes}
              className="inline-flex items-center gap-2 rounded-md border border-brand/40 bg-brand/5 px-4 py-2 text-xs font-bold text-brand transition-colors hover:border-brand disabled:opacity-50"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Importer les demandes
            </button>
            <button
              data-testid="purge-open-btn"
              onClick={() => { setPurgeOpen(true); setPurgeText(""); setPurgeScope("legacy"); }}
              className="inline-flex items-center gap-2 rounded-md border border-red-300 bg-red-50 px-4 py-2 text-xs font-bold text-red-700 transition-colors hover:border-red-500"
            >
              <Trash2 className="h-3.5 w-3.5" /> Tout vider
            </button>
            <button
              data-testid="relance-all-btn"
              onClick={async () => {
                try {
                  const { data } = await api.get("/admin/relance-preview");
                  setRelanceTargets(data);
                } catch (e) {
                  toast.error(apiError(e.response?.data?.detail) || e.message);
                }
              }}
              className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-brand-hover"
            >
              <Send className="h-3.5 w-3.5" /> Relancer tous les sans-réponse
            </button>
            <button
              onClick={() => exportCsv(true)}
              data-testid="export-due-csv-btn"
              className="inline-flex items-center gap-2 rounded-md bg-neutral-900 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-brand"
            >
              <Download className="h-3.5 w-3.5" /> Export commissions dues
            </button>
            <button
              onClick={() => exportCsv(false)}
              data-testid="export-all-csv-btn"
              className="inline-flex items-center gap-2 rounded-md border border-neutral-300 px-4 py-2 text-xs font-bold transition-colors hover:border-brand hover:text-brand"
            >
              <Download className="h-3.5 w-3.5" /> Toutes les commissions
            </button>
          </div>
        </div>

        {stats && (
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-3">
            {[
              ["Leads", stats.total_leads, "stat-total-leads"],
              ["Taux conversion", `${stats.taux_conversion} %`, "stat-conversion"],
              ["Commissions confirmées", `${stats.commissions_confirmees} €`, "stat-commissions"],
              ["Restant à facturer", `${stats.commissions_dues} €`, "stat-due"],
              ["Litiges", stats.litiges, "stat-disputes"],
            ].map(([label, value, tid]) => (
              <div key={label} className="border border-neutral-200 bg-white rounded-lg px-4 py-3">
                <p className="text-[11px] uppercase tracking-[0.14em] text-neutral-400">{label}</p>
                <p className="mt-1 text-xl font-extrabold font-display" data-testid={tid}>
                  {value}
                </p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <button
            onClick={() => { setStatus(""); setReassignMode(false); }}
            data-testid="filter-status-all"
            className={`rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
              status === "" && !reassignMode ? "border-brand bg-brand text-white" : "border-neutral-300 bg-white hover:border-neutral-500"
            }`}
          >
            Tous
          </button>
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => { setStatus(s); setReassignMode(false); }}
              data-testid={`filter-status-${s}`}
              className={`rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
                status === s && !reassignMode ? "border-brand bg-brand text-white" : "border-neutral-300 bg-white hover:border-neutral-500"
              }`}
            >
              {STATUS_LABELS[s]}
            </button>
          ))}
          <button
            onClick={() => { setAlreadyTrained((v) => !v); setReassignMode(false); }}
            data-testid="filter-already-trained"
            className={`rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
              alreadyTrained && !reassignMode ? "border-orange-500 bg-orange-500 text-white" : "border-neutral-300 bg-white hover:border-neutral-500"
            }`}
          >
            Déjà formés (veulent une date)
          </button>
          <button
            onClick={() => { setReassignMode((v) => !v); setStatus(""); setAlreadyTrained(false); }}
            data-testid="filter-to-reassign"
            className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
              reassignMode ? "border-brand bg-brand text-white" : "border-brand/40 bg-brand/5 text-brand hover:border-brand"
            }`}
          >
            <RefreshCw className="h-3.5 w-3.5" /> À réattribuer
          </button>
          <input
            data-testid="filter-city-input"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Filtrer par ville…"
            className="ml-auto w-48 rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-xs outline-none focus:border-brand transition-colors"
          />
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="source-filters">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">Origine</span>
          {SOURCES.map(([k, label]) => (
            <button
              key={k || "all"}
              onClick={() => { setSource(k); setReassignMode(false); }}
              data-testid={`filter-source-${k || "all"}`}
              className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                source === k ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-300 bg-white hover:border-neutral-500"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {selected.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm" data-testid="bulk-bar">
            <span className="font-semibold text-red-800" data-testid="bulk-count">{selected.length} sélectionné(s)</span>
            <button
              onClick={bulkDelete}
              disabled={busy}
              data-testid="bulk-delete-btn"
              className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-red-700 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" /> Supprimer la sélection
            </button>
            <button onClick={() => setSelected([])} data-testid="bulk-clear-btn" className="text-xs font-semibold text-red-700 underline">
              Désélectionner
            </button>
          </div>
        )}

        {purgeOpen && (
          <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 px-4">
            <div className="w-full max-w-lg rounded-xl bg-white p-6" data-testid="purge-modal">
              <h2 className="flex items-center gap-2 text-xl font-extrabold tracking-tight">
                <AlertTriangle className="h-5 w-5 text-red-600" /> Vider les prospects
              </h2>
              <p className="mt-2 text-sm text-neutral-600">
                Suppression définitive des fiches prospects (avec leurs notes et rappels). Les réservations, la liste d&apos;attente et les messages de contact d&apos;origine sont conservés.
              </p>
              <div className="mt-4 space-y-2">
                {[
                  ["legacy", "Seulement les anciens leads (ancien formulaire)"],
                  ["all", "Toutes les fiches prospects (anciennes + nouvelles)"],
                ].map(([k, label]) => (
                  <label key={k} className={`flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2.5 text-sm ${purgeScope === k ? "border-red-500 bg-red-50" : "border-neutral-200"}`}>
                    <input type="radio" name="purge-scope" checked={purgeScope === k} onChange={() => setPurgeScope(k)} data-testid={`purge-scope-${k}`} />
                    {label}
                  </label>
                ))}
              </div>
              <label className="mt-4 block text-sm font-semibold">
                Tape <span className="font-mono text-red-600">VIDER</span> pour confirmer
                <input
                  value={purgeText}
                  onChange={(e) => setPurgeText(e.target.value)}
                  data-testid="purge-confirm-input"
                  className="mt-1.5 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-red-500"
                />
              </label>
              <div className="mt-6 flex gap-3">
                <button
                  disabled={purgeText.trim().toUpperCase() !== "VIDER" || busy}
                  onClick={purge}
                  data-testid="purge-confirm-btn"
                  className="flex-1 rounded-md bg-red-600 px-5 py-3 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-40"
                >
                  {busy ? "Suppression…" : "Supprimer définitivement"}
                </button>
                <button onClick={() => setPurgeOpen(false)} data-testid="purge-cancel-btn"
                  className="rounded-md border border-neutral-300 px-5 py-3 text-sm font-bold hover:border-neutral-900">
                  Annuler
                </button>
              </div>
            </div>
          </div>
        )}

        {relanceTargets && (
          <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 px-4">
            <div className="w-full max-w-lg rounded-xl bg-white p-6" data-testid="relance-confirm-modal">
              <h2 className="flex items-center gap-2 text-xl font-extrabold tracking-tight">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
                Confirmer l'envoi groupé
              </h2>
              <p className="mt-2 text-sm text-neutral-600">
                {relanceTargets.length === 0
                  ? "Aucun élève à relancer : tous ont déjà répondu ou ne sont pas encore attribués."
                  : `${relanceTargets.length} élève(s) vont recevoir un email « T'es-tu inscrit chez … ? ». Vérifie la liste avant d'envoyer.`}
              </p>
              <ul className="mt-4 max-h-60 space-y-1 overflow-y-auto text-sm" data-testid="relance-recipients">
                {relanceTargets.map((t) => (
                  <li key={t.id} className="flex items-center justify-between gap-3 rounded bg-neutral-50 px-3 py-2">
                    <span className="font-semibold">{t.name}</span>
                    <span className="text-neutral-500">{t.email}</span>
                    <span className="text-xs text-neutral-400">{t.school_name}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 flex gap-3">
                <button
                  disabled={relanceTargets.length === 0 || sending}
                  data-testid="relance-confirm-btn"
                  onClick={async () => {
                    setSending(true);
                    try {
                      const { data } = await api.post("/admin/leads/relance-all");
                      toast.success(
                        `${data.sent} relance(s) envoyée(s) sur ${data.candidates} élève(s)` +
                          (data.failed ? ` · ${data.failed} échec(s)` : "")
                      );
                      setRelanceTargets(null);
                      load();
                    } catch (e) {
                      toast.error(apiError(e.response?.data?.detail) || e.message);
                    } finally {
                      setSending(false);
                    }
                  }}
                  className="flex-1 rounded-md bg-brand px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-brand-hover disabled:opacity-50"
                >
                  {sending ? "Envoi…" : `Envoyer les ${relanceTargets.length} relances`}
                </button>
                <button
                  data-testid="relance-cancel-btn"
                  onClick={() => setRelanceTargets(null)}
                  className="rounded-md border border-neutral-300 px-5 py-3 text-sm font-bold transition-colors hover:border-neutral-900"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 overflow-x-auto border border-neutral-200 rounded-lg bg-white">
          <table className="w-full text-sm" data-testid="admin-leads-table">
            <thead className="sticky top-0 bg-neutral-50 text-left text-[11px] uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-3 py-3">
                  <input
                    type="checkbox"
                    aria-label="Tout sélectionner"
                    data-testid="select-all-leads"
                    checked={allSelected}
                    onChange={() => setSelected(allSelected ? [] : leads.map((x) => x.id))}
                  />
                </th>
                <th className="px-4 py-3">Élève</th>
                <th className="px-4 py-3">Ville</th>
                <th className="px-4 py-3">Permis</th>
                <th className="px-4 py-3">Besoin / Délai</th>
                <th className="px-4 py-3">Zones souhaitées</th>
                <th className="px-4 py-3">Auto-école attribuée</th>
                <th className="px-4 py-3">École</th>
                <th className="px-4 py-3">Élève</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3">Emails</th>
                <th className="px-4 py-3">Comm.</th>
                <th className="px-4 py-3">Facturé</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {leads.length === 0 && (
                <tr>
                  <td colSpan={14} className="px-4 py-8 text-neutral-500" data-testid="admin-no-leads">
                    {reassignMode ? "Aucun lead à réattribuer." : "Aucun prospect."}
                  </td>
                </tr>
              )}
              {leads.map((l) => (
                <tr
                  key={l.id}
                  data-testid={`admin-lead-row-${l.id}`}
                  className={`transition-colors ${l.urgent ? "bg-orange-50/60 hover:bg-orange-50" : "hover:bg-neutral-50"}`}
                >
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      aria-label={`Sélectionner ${l.first_name}`}
                      data-testid={`select-lead-${l.id}`}
                      checked={selected.includes(l.id)}
                      onChange={() => toggle(l.id)}
                    />
                  </td>
                  <td className="px-4 py-3 font-semibold whitespace-nowrap">
                    <span
                      data-testid={`lead-source-${l.id}`}
                      className={`mb-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${SOURCE_STYLE[l.source_type || ""] || SOURCE_STYLE[""]}`}
                    >
                      {l.source_label}
                    </span>
                    <br />
                    <Link
                      to={`/admin/leads/${l.id}`}
                      data-testid={`open-lead-${l.id}`}
                      className="inline-flex items-center gap-2 hover:text-brand transition-colors"
                    >
                      {l.first_name} {l.last_name}
                      {l.urgent && (
                        <span
                          data-testid={`urgent-badge-${l.id}`}
                          className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold uppercase text-orange-700"
                        >
                          <Zap className="h-3 w-3" /> Urgent
                        </span>
                      )}
                    </Link>
                    <span className="block text-xs font-normal text-neutral-500">{l.email}</span>
                    {reassignMode && (l.reassign_reasons || []).length > 0 && (
                      <span className="mt-1 flex flex-wrap gap-1" data-testid={`reassign-reasons-${l.id}`}>
                        {l.reassign_reasons.map((r) => (
                          <span key={r} className="rounded-full bg-brand/10 px-2 py-0.5 text-[10px] font-bold text-brand">
                            {r}
                          </span>
                        ))}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {l.city} <span className="text-neutral-400">{l.postal_code}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{l.license_type}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {l.need_type || "—"}
                    <span className="block text-xs text-neutral-500">{l.urgency || "—"}</span>
                  </td>
                  <td className="px-4 py-3 max-w-[180px] text-xs text-neutral-600">
                    {(l.target_zones || []).join(", ") || "—"}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <select
                      data-testid={`assign-school-${l.id}`}
                      value={l.assigned_school_id || ""}
                      onChange={(e) =>
                        e.target.value &&
                        action(() => api.post(`/admin/leads/${l.id}/assign/${e.target.value}`), "Lead attribué — email envoyé à l'auto-école")
                      }
                      className="rounded border border-neutral-300 px-2 py-1 text-xs outline-none focus:border-brand transition-colors"
                    >
                      <option value="">— Attribuer —</option>
                      {schools.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name} ({s.postal_code})
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{l.school_status || "—"}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{l.student_status || "—"}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={l.status} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap" data-testid={`lead-email-status-${l.id}`}>
                    {l.email_status === "failed" ? (
                      <span
                        title={`${l.emails_failed} email(s) en échec`}
                        className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-bold text-red-600"
                      >
                        <MailX className="h-3.5 w-3.5" /> {l.emails_failed} échec
                      </span>
                    ) : l.email_status === "sent" ? (
                      <span
                        title={`${l.emails_sent} email(s) envoyé(s)`}
                        className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700"
                      >
                        <MailCheck className="h-3.5 w-3.5" /> {l.emails_sent}
                      </span>
                    ) : (
                      <span title="Aucun email" className="inline-flex items-center gap-1 text-[11px] text-neutral-400">
                        <Mail className="h-3.5 w-3.5" /> —
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{l.commission_amount ?? "—"} €</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => action(() => api.post(`/admin/leads/${l.id}/invoiced`), "Statut facturation mis à jour")}
                      data-testid={`toggle-invoiced-${l.id}`}
                      className={`rounded px-2 py-1 text-xs font-bold transition-colors ${
                        l.invoiced ? "bg-emerald-50 text-emerald-700" : "bg-neutral-100 text-neutral-500 hover:bg-neutral-200"
                      }`}
                    >
                      <Receipt className="inline h-3 w-3 mr-1" />
                      {l.invoiced ? "Oui" : "Non"}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => action(() => api.post(`/admin/leads/${l.id}/relance`), "Relance élève déclenchée")}
                        data-testid={`relance-${l.id}`}
                        title="Relancer l'élève"
                        className="rounded border border-neutral-300 p-1.5 text-neutral-600 hover:border-brand hover:text-brand transition-colors"
                      >
                        <Send className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(`Supprimer définitivement le lead ${l.first_name} ${l.last_name} ?`)) {
                            action(() => api.delete(`/admin/leads/${l.id}`), "Lead supprimé");
                          }
                        }}
                        data-testid={`delete-lead-${l.id}`}
                        title="Supprimer ce lead"
                        className="rounded border border-neutral-300 p-1.5 text-neutral-500 hover:border-red-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                      {l.status === "arbitrage" && (
                        <>
                          <button
                            onClick={() => action(() => api.post(`/admin/leads/${l.id}/arbitrate`, { decision: "confirme" }), "Litige tranché : confirmé")}
                            data-testid={`arbitrate-confirm-${l.id}`}
                            className="rounded bg-emerald-600 px-2 py-1 text-xs font-bold text-white transition-colors hover:bg-emerald-700"
                          >
                            <Gavel className="inline h-3 w-3 mr-1" />
                            Valider
                          </button>
                          <button
                            onClick={() => action(() => api.post(`/admin/leads/${l.id}/arbitrate`, { decision: "rejete" }), "Litige tranché : rejeté")}
                            data-testid={`arbitrate-reject-${l.id}`}
                            className="rounded border border-red-300 px-2 py-1 text-xs font-bold text-red-600 transition-colors hover:bg-red-50"
                          >
                            Rejeter
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Shell>
  );
}
